import dayjs from "dayjs";
import {
  Appointment,
  Route,
  Section,
  Shipment,
} from "../../../services/prePlanningService.types";
import type {
  AppointmentRequiredShipmentDetail,
  AssociatedShipment,
  CallAttempt,
  PreviousDeliveries,
} from "../../../types/inbound/prePlanning/aptrShipments.type";
import { Trap as PlannedTrap } from "../../../types/planning/plannedTrap.type";
import { ShipmentWithSequence } from "../../../types/planning/shipmentSeq.type";
import { Trap } from "../../../types/planning/trap.type";
import { tags } from "../../tags";

export const mockSectionsData = {
  empty: [],
  default: [
    {
      id: 1,
      title: "WILL CALL",
      name: null,
      shipmentsCount: 2,
      shipmentsPallets: 5,
      shipmentsPieces: 5,
      shipmentsHandlingUnits: 5,
      handlingUnits: 5,
      shipmentsWeight: 400,
      shipmentIds: [1, 2],
      shipmentsWeightUnit: "lbs",
      shipmentsTags: tags,
      bills: 2,
      pallets: 5,
      pieces: 5,
      weight: 400,
      weightUnit: "lbs",
      tags,
    },
    {
      id: 7,
      title: "OS&D",
      name: null,
      shipmentsCount: 1,
      shipmentsPallets: 4,
      shipmentsPieces: 4,
      shipmentsHandlingUnits: 4,
      handlingUnits: 4,
      shipmentsWeight: 800,
      shipmentIds: [3],
      shipmentsWeightUnit: "lbs",
      shipmentsTags: tags,
      bills: 1,
      pallets: 4,
      pieces: 4,
      weight: 800,
      weightUnit: "lbs",
      tags,
    },
  ],
} as const satisfies Record<string, Section[]>;

export const mockShipment: ShipmentWithSequence = {
  id: 1,
  shipmentId: 1,
  tripNumber: "3333",
  isPlanned: false,
  proNumber: "11231",
  serviceDue: new Date("2022-05-04T17:00:00"),
  serviceDueDate: new Date("2022-05-04T17:00:00"),
  appointmentDate: new Date("2022-05-04T17:00:00"),
  location: null,
  weight: 1000,
  pallets: 2,
  pieces: 3,
  handlingUnits: 3,
  weightUnit: "lbs",
  tags: [],
  destinationSicCode: "MIA",
  shipper: {
    id: 1,
    name: "Paul",
  },
  consignee: {
    id: 2,
    name: "Not Paul",
    address: "Not Paul's House",
    city: "Rome",
    state: "Palermo",
    zipCode: "40402",
  },
  sic: "ONT",
  status: "FULL",
  appointmentEndDate: new Date("2022-05-04T17:00:00"),
  stopSeq: 1,
  plannedStopSeq: 1,
  etaDate: new Date("2022-05-04T17:00:00"),
  isManualAssign: false,
  highlightedRow: false,
  presetRouteId: 1,
  presetRouteName: "Route 1",
  futurePlanDates: [],
  isAvailableToReconcile: true,
  assignment: null,
  assignmentId: 312316,
  type: "delivery_route",
  isCommitted: true,
  doh: 0,
};

export const mockShipmentData = {
  default: [
    {
      id: 1,
      proNumber: "987654321",
      tripNumber: 9843833,
      shipper: { id: 1, name: "Shipper 1" },
      consignee: {
        id: 1,
        name: "Heavy Co. 1",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 2,
      pieces: 3,
      handlingUnits: 3,
      weight: 400,
      weightUnit: "lbs",
      tags,
      appointmentDate: new Date(2023, 2, 1),
      appointmentEndDate: new Date(2023, 2, 2),
      appointmentTime: 1000,
      serviceDue: new Date(2023, 2, 6),
      location: "On Dock",
      sic: "ETA 2 hours, ONT",
      status: "Trap",
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: false,
      presetRouteId: 1,
      presetRouteName: "Route 1",
      destinationSicCode: "ONT",
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 6),
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 1,
      plannedStopSeq: 1,
      assignmentId: 312316,
      type: "delivery_route",
      isCommitted: true,
      serviceLevel: "MXUS-LTL",
      careOfDelivery: {
        id: 5039,
        name: "C/O NAME",
        address: "C/O ADDRESS",
        city: "C/O CITY",
        state: "C/O STATE",
        zipCode: "C/O ZIP",
      },
    },
    {
      id: 2,
      proNumber: "987654322",
      tripNumber: 4948849,
      shipper: { id: 2, name: "Shipper 2" },
      consignee: {
        id: 1,
        name: "Heavy Co. 2",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 3,
      pieces: 2,
      handlingUnits: 2,
      weight: 450,
      weightUnit: "lbs",
      tags: [{ name: "APPT", colorHex: "#20e0aa", description: "Appointment" }],
      appointmentDate: new Date(2023, 2, 2),
      appointmentEndDate: new Date(2023, 2, 3),
      appointmentTime: 1100,
      serviceDue: new Date(2023, 2, 5),
      location: "On Dock",
      sic: null,
      status: "Enrt",
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: true,
      presetRouteId: 2,
      presetRouteName: "Route 2",
      destinationSicCode: null,
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 5),
      futurePlanDates: [20240417],
      isAvailableToReconcile: true,
      stopSeq: 2,
      plannedStopSeq: 2,
      assignmentId: 312316,
      type: "delivery_route",
      isCommitted: true,
    },
    {
      id: 3,
      tripNumber: 8772822,
      proNumber: "987654323",
      shipper: { id: 1, name: "Shipper 1" },
      consignee: {
        id: 1,
        name: "Heavy Co. 3",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 4,
      pieces: 4,
      handlingUnits: 4,
      weight: 500,
      weightUnit: "lbs",
      tags,
      appointmentDate: new Date(2023, 2, 3),
      appointmentEndDate: new Date(2023, 2, 5),
      appointmentTime: 1200,
      serviceDue: new Date(2023, 2, 4),
      assignment: "AZUSA-3",
      location: "On Dock",
      sic: "ONT",
      status: "Enrt",
      etaDate: null,
      isPlanned: false,
      presetRouteId: 3,
      presetRouteName: "Route 3",
      destinationSicCode: "ONT",
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 4),
      futurePlanDates: [20240417],
      isAvailableToReconcile: true,
      stopSeq: 3,
      plannedStopSeq: 3,
      assignmentId: 312315,
      type: "delivery_route",
      isCommitted: true,
      serviceLevel: "MXUS-LTL",
      careOfDelivery: {
        id: 44,
        name: "Heavy Co. Mexico",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
    },
    {
      id: 4,
      proNumber: "987654324",
      tripNumber: 12473812,
      shipper: { id: 2, name: "Shipper 2" },
      consignee: {
        id: 1,
        name: "Heavy Co.",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 1,
      pieces: 1,
      handlingUnits: 1,
      weight: 400,
      weightUnit: "lbs",
      tags: [{ name: "APPT", colorHex: "#20e0aa", description: "Appointment" }],
      appointmentDate: new Date(2023, 2, 4),
      appointmentEndDate: new Date(2023, 2, 5),
      appointmentTime: 1300,
      serviceDue: new Date(2023, 2, 3),
      assignment: "ANA-SHAGG-4",
      location: "135-235123",
      sic: "ONT",
      status: "Enrt",
      etaDate: null,
      isPlanned: false,
      presetRouteId: 4,
      presetRouteName: "Route 4",
      destinationSicCode: "ONT",
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 3),
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 4,
      assignmentId: null,
      type: null,
      isCommitted: false,
    },
    {
      id: 5,
      proNumber: "987654325",
      tripNumber: 7363783,
      shipper: { id: 1, name: "Shipper 1" },
      consignee: {
        id: 1,
        name: "Heavy Co.",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 2,
      pieces: 2,
      handlingUnits: 2,
      weight: 420,
      weightUnit: "lbs",
      tags,
      appointmentDate: new Date(2023, 2, 5),
      appointmentEndDate: new Date(2023, 2, 6),
      appointmentTime: 1400,
      serviceDue: new Date(2023, 2, 2),
      location: "135-164734",
      sic: "ONT",
      status: "Trap",
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: false,
      presetRouteId: 5,
      presetRouteName: "Route 5",
      destinationSicCode: "ONT",
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 2),
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 5,
      assignmentId: null,
      type: null,
      isCommitted: false,
    },
    {
      id: 6,
      proNumber: "987654326",
      tripNumber: 87475732,
      shipper: { id: 2, name: "Shipper 2" },
      consignee: {
        id: 1,
        name: "Heavy Co.",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 5,
      pieces: 3,
      handlingUnits: 3,
      weight: 400,
      weightUnit: "lbs",
      tags: [{ name: "APPT", colorHex: "#20e0aa", description: "Appointment" }],
      appointmentDate: new Date(2023, 2, 6),
      appointmentEndDate: new Date(2023, 2, 7),
      appointmentTime: 1500,
      serviceDue: new Date(2023, 2, 1),
      location: "On Dock",
      sic: "ONT",
      status: "Trap",
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: true,
      presetRouteId: 6,
      presetRouteName: "Route 6",
      destinationSicCode: "ONT",
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 1),
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 6,
      assignmentId: null,
      type: null,
      isCommitted: false,
    },
    {
      id: 7,
      doh: 1,
      proNumber: "110930229",
      tripNumber: 87475732,
      shipper: { id: 2, name: "DGP" },
      consignee: {
        id: 1,
        name: "T3 LINING SUPPLY",
        address: "17605 FABRICA WAY STE B",
        city: "CERRITOS",
        state: "CA",
        zipCode: "90703",
      },
      delivery: {
        name: "Heavy Co. 1",
        address: "67 Way Ave.",
        city: "Culver City",
        state: "CA",
        zipCode: "92807",
      },
      pallets: 5,
      pieces: 3,
      handlingUnits: 3,
      weight: 400,
      weightUnit: "lbs",
      tags: [
        {
          name: "APTR",
          colorHex: "#777DE9",
          description: "Appointment required",
        },
      ],
      appointmentDate: new Date(2023, 2, 6),
      appointmentEndDate: new Date(2023, 2, 7),
      appointmentTime: 1500,
      serviceDue: new Date(2023, 2, 1),
      location: "On Dock",
      sic: "ONT",
      status: "Trap",
      assignmentId: null,
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: true,
      presetRouteId: 7,
      presetRouteName: "Route 7",
      destinationSicCode: "ONT",
      isManualAssign: false,
      serviceDueDate: new Date(2023, 2, 1),
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 7,
      type: null,
      isCommitted: false,
    },
    {
      id: 8,
      doh: 3,
      proNumber: "110930228",
      tripNumber: 87475733,
      shipper: { id: 3, name: "AMERICAN BOTANICALS" },
      consignee: {
        id: 1,
        name: "BAE SYSTEMS SAN DIEGO",
        address: "1650 INDUSTRIAL BLVD",
        city: "CHULA VISTA",
        state: "CA",
        zipCode: "91911",
      },
      delivery: {
        id: 1,
        name: "TRACY SIGNS ",
        address: "3771 W 11TH ST ",
        city: "TRACY ",
        state: "CA",
        zipCode: "95304",
      },
      pallets: 2,
      pieces: 1,
      handlingUnits: 1,
      weight: 125,
      weightUnit: "lbs",
      tags: [
        {
          name: "APTR",
          colorHex: "#777DE9",
          description: "Appointment required",
        },
      ],
      appointmentDate: null,
      appointmentEndDate: null,
      serviceDue: new Date(2023, 1, 2),
      serviceDueDate: new Date(2023, 1, 2),
      location: "On Dock",
      sic: "ONT",
      status: "Trap",
      assignmentId: null,
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: false,
      presetRouteId: 8,
      presetRouteName: "Route 8",
      destinationSicCode: "ONT",
      isManualAssign: false,
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 8,
      type: null,
      isCommitted: false,
    },
    {
      id: 9,
      doh: 5,
      proNumber: "110930227",
      tripNumber: 87475734,
      pallets: 4,
      pieces: 0,
      handlingUnits: 0,
      weight: 0,
      weightUnit: "lbs",
      tags: [
        {
          name: "APTR",
          colorHex: "#777DE9",
          description: "Appointment required",
        },
      ],
      appointmentDate: null,
      appointmentEndDate: null,
      serviceDue: new Date(2023, 4, 6),
      serviceDueDate: new Date(2023, 4, 6),
      location: "On Dock",
      sic: "ONT",
      status: "Trap",
      assignmentId: null,
      assignment: "Unassigned",
      etaDate: null,
      isPlanned: true,
      presetRouteId: 9,
      presetRouteName: "Route 9",
      destinationSicCode: "ONT",
      isManualAssign: false,
      futurePlanDates: [],
      isAvailableToReconcile: true,
      stopSeq: 9,
      type: null,
      isCommitted: false,
    },
  ],
} as const satisfies Record<string, Shipment[]>;

export const mockAppointmentRequiredShipmentsDetails = {
  "110930229": {
    consignee: {
      id: 21249584,
      name: "T3 LINING SUPPLY",
      address: "17605 FABRICA WAY STE B",
      city: "CERRITOS",
      state: "CA",
      zip: "90703",
      refClientCode: "21249584",
      contactName: null,
      contactPhone: "909 320 6444",
      contactEmail: null,
      lat: 33.871105,
      lng: -118.032166,
      website: "https://www.t3liningsupply.com/",
    },
    shipper: {
      id: 1,
      name: "DGP",
      refClientCode: "22444754",
      address: "2617 GOODWIN LANE SUITE 105",
      city: "NEW BRAUNFELS",
      state: "TX",
      zip: "78130",
      contactName: null,
      contactPhone: null,
      contactEmail: null,
    },
    billTo: {
      id: 1,
      name: "Heavy Co. 1",
      address: "67 Way Ave.",
      city: "Culver City",
      state: "CA",
      zip: "92807",
      refClientCode: "12144600",
      contactName: "DAVID LUU",
      contactPhone: "949 245 1761",
      contactEmail: "DAVID.LUU@DKBRANDS.US",
    },
    deliveryHours: {
      startUtc: dayjs("2026-04-10").hour(15).toDate(),
      endUtc: dayjs("2026-04-11").hour(0).toDate(),
    },
    createdByLoginId: "TLIVERMAN",
    chargeType: "Collect",
    collectAmount: 612.6,
    purchaseOrder: null,
    shipDate: dayjs("2026-04-07").hour(5).toDate(),
    itemsSummary: {
      pallets: 5,
      pieces: 3,
      handlingUnits: 3,
      weight: 400,
      dimensions: [
        {
          seqNumber: 1,
          handlingUnits: 1,
          length: 32,
          width: 32,
          height: 32,
        },
        {
          seqNumber: 2,
          handlingUnits: 1,
          length: 32,
          width: 32,
          height: 32,
        },
        {
          seqNumber: 3,
          handlingUnits: 1,
          length: 32,
          width: 32,
          height: 32,
        },
      ],
    },
    descriptionOfArticles: [
      {
        seqNumber: 96155938,
        saidToContain: "31 PCS",
        description: "KITCHEN TOOLS",
        commodityCode: "101025-4",
        pieces: 0,
        weight: 220,
      },
      {
        seqNumber: 96155958,
        saidToContain: "60 PCS",
        description: "KITCHEN TOOLS",
        commodityCode: "101025-4",
        pieces: 0,
        weight: 132,
      },
      {
        seqNumber: 96155969,
        saidToContain: "108 PCS",
        description: "KITCHEN TOOLS",
        commodityCode: "101025-4",
        pieces: 0,
        weight: 343,
      },
    ],
    doh: 5,
    proNotes: [
      {
        id: 1,
        timestamp: dayjs("2026-04-08").hour(0).minute(44).second(22).toDate(),
        csrLoginId: null,
        terminalCode: null,
        content: null,
      },
    ],
    drNotes: [
      {
        id: 1,
        timestamp: dayjs("2026-04-07").hour(21).minute(54).second(58).toDate(),
        recordingParty: null,
        terminalCode: null,
        content: "LH RTS, O/P 161928981\r\n",
      },
    ],
  },
  "110930228": {
    consignee: {
      id: 502625,
      name: "BAE SYSTEMS SAN DIEGO",
      address: "1650 INDUSTRIAL BLVD",
      city: "CHULA VISTA",
      state: "CA",
      zip: "91911",
      refClientCode: "502625",
      contactName: "ROXANNE BENNETT",
      contactPhone: "858 453 7845",
      contactEmail: "roxanneb@petco.com",
      lat: 32.597157,
      lng: -117.085564,
      website: "https://www.baesystems.com/",
    },
    shipper: {
      id: 2,
      name: "AMERICAN BOTANICALS",
      refClientCode: null,
      address: "2617 GOODWIN LANE SUITE 105",
      city: "NEW BRAUNFELS",
      state: "TX",
      zip: "78130",
      contactName: "CUSTOMER SERVICE",
      contactPhone: "330 682 7225",
      contactEmail: "",
    },
    billTo: {
      id: 2,
      name: "TRACY SIGNS ",
      address: "3771 W 11TH ST ",
      city: "TRACY ",
      state: "CA",
      zip: "95304",
      refClientCode: "21737981",
      contactName: "CUSTOMER SERVICE",
      contactPhone: "330 682 0099",
      contactEmail: "CUSTOMERSERVICE@JARRETTLOGISTICS.COM",
    },
    deliveryHours: {
      startUtc: dayjs("2026-04-10").hour(15).toDate(),
      endUtc: dayjs("2026-04-11").hour(0).toDate(),
    },
    chargeType: "Collect",
    createdByLoginId: "TLIVERMAN",
    collectAmount: 523.64,
    purchaseOrder: "29605672",
    shipDate: dayjs("2026-04-07").hour(5).toDate(),
    itemsSummary: {
      pallets: 2,
      pieces: 1,
      handlingUnits: 1,
      weight: 125,
      dimensions: [
        {
          seqNumber: 4,
          handlingUnits: 1,
          length: 23,
          width: 34,
          height: 11,
        },
      ],
    },
    descriptionOfArticles: [
      {
        seqNumber: 93722685,
        saidToContain: "178 CTN",
        description: "PET TREAT CL100",
        commodityCode: "27765-6",
        pieces: 0,
        weight: 945,
      },
    ],
    doh: 3,
    proNotes: [
      {
        id: 2,
        timestamp: dayjs("2026-04-08").hour(0).minute(44).second(22).toDate(),
        csrLoginId: null,
        terminalCode: null,
        content: null,
      },
    ],
    drNotes: [
      {
        id: 2,
        timestamp: dayjs("2025-11-01").hour(3).minute(1).second(12).toDate(),
        recordingParty: null,
        terminalCode: null,
        content:
          "•\tAPPT MADE. MONDAY 11/03/2025 10AM.  \r\nMUST BE SCHEDULED 72 HOURS IN ADVANCE\r\nREQUIRED DELIVERY DATE 11/6/2025\r\n",
      },
      {
        id: 3,
        timestamp: dayjs("2025-10-31").hour(17).minute(8).second(51).toDate(),
        recordingParty: null,
        terminalCode: null,
        content:
          "•\tAPPT MADE. THRUSDAY 11/06/2025 10AM.   \r\nMUST BE SCHEDULED 72 HOURS IN ADVANCE\r\nREQUIRED DELIVERY DATE 11/6/2025\r\n",
      },
      {
        id: 4,
        timestamp: dayjs("2025-10-31").hour(15).minute(37).second(57).toDate(),
        recordingParty: null,
        terminalCode: null,
        content:
          "•\tAPPT MADE. TUESDAY 11/04/2025 10AM.  \r\nMUST BE SCHEDULED 72 HOURS IN ADVANCE\r\nREQUIRED DELIVERY DATE 11/6/2025\r\n",
      },
    ],
  },
  "110930227": {
    consignee: null,
    shipper: null,
    billTo: null,
    deliveryHours: {
      startUtc: dayjs("2026-04-10").hour(15).toDate(),
      endUtc: dayjs("2026-04-11").hour(0).toDate(),
    },
    chargeType: "Collect",
    createdByLoginId: null,
    collectAmount: 484.53,
    purchaseOrder: "676064-0900",
    shipDate: dayjs("2026-04-07").hour(5).toDate(),
    itemsSummary: {
      pallets: 4,
      pieces: 0,
      handlingUnits: 0,
      weight: 0,
    },
    descriptionOfArticles: [
      {
        seqNumber: 93719916,
        saidToContain: null,
        description: "CLOTHING 4-6 PCF CL 175",
        commodityCode: "49880-4",
        pieces: 0,
        weight: 858,
      },
    ],
    doh: 1,
    proNotes: [
      {
        id: 3,
        timestamp: dayjs("2025-10-31").hour(14).minute(26).second(52).toDate(),
        csrLoginId: null,
        terminalCode: null,
        content:
          "\r\n---SVC_TMWEB-USER-LWINKFIELD---Oct 27, 2025 9:42:59 AM\r\nAPPOINTMENT ___ COMMODITY_ RETAIL \r\nPRODUCT______ ###### GEOTRACKING VIA P44 OR \r\nTRUCKER TOOLS REQUESTED\r\n\r\nLMARISCAL - Oct 30,2025 10:55 AM\r\nFrom: Mariscal, Lisa \r\nSent: Thursday, October 30, 2025 10:55 AM\r\nTo: Boot Barn Support Team <bootbarn@bluegracegroup.com>\r\nCc: ONT APPTS Mailbox <ONTAPPTS@dylt.com>\r\nSubject: PACKING LIST REQUEST PRO 161536057 BOL BG1039169625 PO# 676064-\r\n0900 \r\n\r\n\r\nHello,\r\n\r\nPlease send a copy of the Packing List for Pro Number:161536057  in order to request the \r\nappointment before PO Expiration.\r\n\r\nThank You\r\n\r\nLisa Mariscal \r\nAppointments-Customer Service Representative - ONT\r\n\r\nLMARISCAL - Oct 31,2025 7:26 AM\r\n________________________________________\r\nGood morning,\r\n\r\nThis has been scheduled for 11.3 at 12pm.\r\n\r\nKimberly Mora |  \r\nMon-Fri 6am-2:15pm\r\n11251 Beech Ave. Suite 200\r\nFontana, CA 92337\r\nkmora@bootbarn.com\r\n",
      },
      {
        id: 4,
        timestamp: dayjs("2025-10-30").hour(17).minute(55).second(59).toDate(),
        csrLoginId: null,
        terminalCode: null,
        content:
          "\r\n---SVC_TMWEB-USER-LWINKFIELD---Oct 27, 2025 9:42:59 AM\r\nAPPOINTMENT ___ COMMODITY_ RETAIL \r\nPRODUCT______ ###### GEOTRACKING VIA P44 OR \r\nTRUCKER TOOLS REQUESTED\r\n\r\nLMARISCAL - Oct 30,2025 10:55 AM\r\nFrom: Mariscal, Lisa \r\nSent: Thursday, October 30, 2025 10:55 AM\r\nTo: Boot Barn Support Team <bootbarn@bluegracegroup.com>\r\nCc: ONT APPTS Mailbox <ONTAPPTS@dylt.com>\r\nSubject: PACKING LIST REQUEST PRO 161536057 BOL BG1039169625 PO# 676064-\r\n0900 \r\n\r\n\r\nHello,\r\n\r\nPlease send a copy of the Packing List for Pro Number:161536057  in order to request the \r\nappointment before PO Expiration.\r\n\r\nThank You\r\n\r\nLisa Mariscal \r\nAppointments-Customer Service Representative - ONT\r\n",
      },
    ],
    drNotes: [
      {
        id: 5,
        timestamp: dayjs("2025-10-31").hour(14).minute(27).second(3).toDate(),
        recordingParty: null,
        terminalCode: null,
        content:
          "This has been scheduled for 11.3 at 12pm.\r\n\r\nKimberly Mora | \r\nMon-Fri 6am-2:15pm\r\n11251 Beech Ave. Suite 200\r\nFontana, CA 92337\r\nkmora@bootbarn.com <mailto:kmora@bootbarn.com>\r\n\r\n",
      },
      {
        id: 6,
        timestamp: dayjs("2025-10-28").hour(1).minute(44).second(10).toDate(),
        recordingParty: null,
        terminalCode: null,
        content: "CONS EMAIL: DCAPPTS@BOOTBARN.COM\r\nHOURS: 8AM - 3PM\r\n",
      },
    ],
  },
} as const satisfies Record<string, AppointmentRequiredShipmentDetail>;

export const mockPreviousDeliveries = {
  default: [
    {
      consigneeId: 21249584,
      shipmentId: 1,
      proNumber: "928374521",
      city: "CERRITOS",
      state: "CA",
      consigneeName: "T3 LINING SUPPLY",
      deliveryDateUtc: dayjs("02-03-2026").hour(15).minute(0).toDate(),
    },
    {
      consigneeId: 21249584,
      shipmentId: 2,
      proNumber: "007775477",
      city: "CERRITOS",
      state: "CA",
      consigneeName: "T3 LINING SUPPLY",
      deliveryDateUtc: dayjs("02-03-2026").hour(12).minute(20).toDate(),
    },
    {
      consigneeId: 21249584,
      shipmentId: 3,
      proNumber: "789424648",
      city: "CERRITOS",
      state: "CA",
      consigneeName: "T3 LINING SUPPLY",
      deliveryDateUtc: dayjs("02-03-2026").hour(8).minute(10).toDate(),
    },
    {
      consigneeId: 502625,
      shipmentId: 4,
      proNumber: "228912465",
      city: "CHULA VISTA",
      state: "CA",
      consigneeName: "BAE SYSTEMS SAN DIEGO",
      deliveryDateUtc: dayjs("02-03-2026").hour(2).minute(0).toDate(),
    },
  ],
} as const satisfies Record<string, PreviousDeliveries[]>;

export const mockCallAttempts = {
  default: [
    {
      id: 7,
      outcome: "NO_ANSWER",
      otherReason: null,
      notes: "Called 2x but no answer",
      attemptedBy: "TLIVERMAN",
      attemptedOn: dayjs("2026-05-06").hour(16).minute(18).second(24).toDate(),
    },
    {
      id: 7,
      outcome: "LEFT_VM",
      otherReason: null,
      notes: "Left voicemail to remind the urgency",
      attemptedBy: "TLIVERMAN",
      attemptedOn: dayjs("2026-05-02").hour(18).minute(1).second(54).toDate(),
    },
    {
      id: 8,
      outcome: "NO_ANSWER",
      otherReason: null,
      notes: "Called 2x but no answer",
      attemptedBy: "TLIVERMAN",
      attemptedOn: dayjs("2026-05-04").hour(13).minute(12).second(43).toDate(),
    },
    {
      id: 8,
      outcome: "REFUSED",
      otherReason: null,
      notes: "Refused the call",
      attemptedBy: "TLIVERMAN",
      attemptedOn: dayjs("2026-05-01").hour(8).minute(8).second(13).toDate(),
    },
  ],
} as const satisfies Record<string, CallAttempt[]>;

export const mockTrapSummaryData: Record<"empty" | "default", Trap[]> = {
  empty: [],
  default: [
    {
      id: 1,
      name: "MON-0700",
      title: "TRAP-000001",
      doorId: 24,
      shipmentsCount: 2,
      shipmentsPallets: 4,
      shipmentsPieces: 4,
      shipmentsHandlingUnits: 4,
      handlingUnits: 4,
      shipmentsWeight: 800,
      shipmentsWeightUnit: "lbs",
      shipmentIds: [1, 2],
      notes: null,
      shipmentsTags: [],
      trailerNumber: null,
      appointmentDate: new Date(2023, 2, 1),
      serviceDueDate: new Date(2023, 2, 1),
      allShipmentsPlanned: false,
      allShipmentsPlannedForFuture: false,
      someShipmentsPlanned: true,
      bills: 2,
      pallets: 4,
      pieces: 4,
      status: "TRAP",
      tags: [],
      weight: 800,
      weightUnit: "lbs",
      allFuturePlanDates: [20240417, 20240418, 20240419],
      trailer: null,
      straightTruck: null,
    },
    {
      id: 2,
      name: "TUE-0630",
      title: "TRAP-000002",
      doorId: 25,
      shipmentsCount: 1,
      shipmentsPallets: 2,
      shipmentsPieces: 2,
      shipmentsHandlingUnits: 2,
      handlingUnits: 2,
      shipmentsWeight: 400,
      shipmentsWeightUnit: "lbs",
      shipmentIds: [6],
      notes: null,
      shipmentsTags: [],
      trailerNumber: null,
      appointmentDate: new Date(2023, 2, 1),
      serviceDueDate: new Date(2023, 2, 1),
      allShipmentsPlanned: true,
      allShipmentsPlannedForFuture: false,
      someShipmentsPlanned: false,
      bills: 1,
      pallets: 2,
      pieces: 2,
      status: "TRAP",
      tags: [],
      weight: 400,
      weightUnit: "lbs",
      allFuturePlanDates: [20240417, 20240418, 20240419],
      trailer: {
        id: 2895,
        name: "ONTGHOST08",
      },
      straightTruck: null,
    },
    {
      id: 3,
      shipmentIds: [],
      title: "TRAP-000003",
      notes: null,
      name: "WED-0600",
      doorId: 18,
      trailerNumber: "LAX038",
      shipmentsPieces: 0,
      shipmentsHandlingUnits: 0,
      handlingUnits: 0,
      shipmentsPallets: 0,
      shipmentsWeight: 0,
      shipmentsWeightUnit: "lbs",
      shipmentsCount: 0,
      appointmentDate: new Date(2023, 2, 1),
      serviceDueDate: new Date(2023, 2, 1),
      shipmentsTags: [],
      allShipmentsPlanned: false,
      allShipmentsPlannedForFuture: false,
      someShipmentsPlanned: false,
      allFuturePlanDates: [],
      trailer: {
        id: 24763,
        name: "LAX038",
      },
      straightTruck: null,
      tags: [],
      status: "TRAP",
      bills: 0,
      pallets: 0,
      pieces: 0,
      weight: 0,
      weightUnit: "lbs",
    },
  ],
};

export const mockPlannedTraps = {
  empty: [],
  default: [
    {
      id: 84152,
      name: "R-LAX038",
      title: "TRAP-084152",
      type: "Physical",
      status: "TRAP",
      bills: 1,
      pallets: 1,
      pieces: 10,
      handlingUnits: 10,
      weight: 1000,
    },
    {
      id: 84151,
      name: "R-LAX044",
      title: "TRAP-084151",
      type: "Physical",
      status: "TRAP",
      bills: 2,
      pallets: 2,
      pieces: 20,
      handlingUnits: 20,
      weight: 2000,
    },
    {
      id: 83669,
      name: "R-LAX044",
      title: "TRAP-083669",
      type: "Physical",
      status: "TRAP",
      bills: 3,
      pallets: 3,
      pieces: 10,
      handlingUnits: 10,
      weight: 1000,
    },
  ],
} as const satisfies Record<string, PlannedTrap[]>;

export const mockAppointmentBucketTableData = {
  empty: [],
  default: [
    {
      planDate: new Date("2024-03-17"),
      title: "A-01",
      shipmentIds: [1],
      shipmentsCount: 1,
      shipmentsPallets: 12,
      shipmentsPieces: 15,
      shipmentsHandlingUnits: 15,
      handlingUnits: 15,
      shipmentsWeight: 200,
      shipmentsWeightUnit: "lbs",
      bills: 1,
      pallets: 12,
      pieces: 15,
      weight: 200,
      weightUnit: "lbs",
      allShipmentsPlanned: false,
      plannedShipmentCount: 1,
      plannedWeight: 100,
      plannedPieces: 8,
      plannedHandlingUnits: 8,
      plannedPallets: 6,
    },
    {
      planDate: new Date("2024-03-17"),
      title: "A-02",
      shipmentIds: [2, 3, 4, 5],
      shipmentsCount: 4,
      shipmentsPallets: 11,
      shipmentsPieces: 12,
      shipmentsHandlingUnits: 12,
      handlingUnits: 12,
      shipmentsWeight: 2570,
      shipmentsWeightUnit: "lbs",
      bills: 4,
      pallets: 11,
      pieces: 12,
      weight: 2570,
      weightUnit: "lbs",
      allShipmentsPlanned: false,
      plannedShipmentCount: 2,
      plannedWeight: 1200,
      plannedPieces: 6,
      plannedHandlingUnits: 6,
      plannedPallets: 5,
    },
    {
      planDate: new Date("2024-03-17"),
      title: "A-03",
      shipmentIds: [3, 4, 5, 6],
      shipmentsCount: 4,
      shipmentsPallets: 1110,
      shipmentsPieces: 12,
      shipmentsHandlingUnits: 12,
      handlingUnits: 12,
      shipmentsWeight: 2570,
      shipmentsWeightUnit: "lbs",
      bills: 4,
      pallets: 1110,
      pieces: 12,
      weight: 504,
      weightUnit: "lbs",
      allShipmentsPlanned: false,
      plannedShipmentCount: 3,
      plannedWeight: 400,
      plannedPieces: 9,
      plannedHandlingUnits: 9,
      plannedPallets: 800,
    },
    {
      planDate: new Date("2024-03-17"),
      title: "A-04",
      shipmentIds: [2, 3, 4, 5, 6],
      shipmentsCount: 5,
      shipmentsPallets: 5,
      shipmentsPieces: 17,
      shipmentsHandlingUnits: 17,
      handlingUnits: 17,
      shipmentsWeight: 2000,
      shipmentsWeightUnit: "lbs",
      bills: 5,
      pallets: 5,
      pieces: 17,
      weight: 2000,
      weightUnit: "lbs",
      allShipmentsPlanned: false,
      plannedShipmentCount: 4,
      plannedWeight: 1600,
      plannedPieces: 14,
      plannedHandlingUnits: 14,
      plannedPallets: 4,
    },
    {
      planDate: new Date("2024-03-17"),
      title: "A-05",
      shipmentIds: [1, 2, 3],
      shipmentsCount: 3,
      shipmentsPallets: 10,
      shipmentsPieces: 20,
      shipmentsHandlingUnits: 20,
      handlingUnits: 20,
      shipmentsWeight: 1500,
      shipmentsWeightUnit: "lbs",
      bills: 3,
      pallets: 10,
      pieces: 20,
      weight: 1500,
      weightUnit: "lbs",
      allShipmentsPlanned: false,
      plannedShipmentCount: 2,
      plannedWeight: 1000,
      plannedPieces: 15,
      plannedHandlingUnits: 15,
      plannedPallets: 8,
    },
    {
      planDate: null,
      title: "APTR",
      shipmentIds: [7, 8, 9],
      shipmentsCount: 3,
      shipmentsPallets: 10,
      shipmentsPieces: 20,
      shipmentsHandlingUnits: 20,
      handlingUnits: 20,
      shipmentsWeight: 1500,
      shipmentsWeightUnit: "lbs",
      bills: 3,
      pallets: 10,
      pieces: 20,
      weight: 1500,
      weightUnit: "lbs",
      allShipmentsPlanned: false,
      plannedShipmentCount: 2,
      plannedWeight: 1000,
      plannedPieces: 15,
      plannedHandlingUnits: 15,
      plannedPallets: 8,
    },
  ],
} as const satisfies Record<string, Appointment[]>;

export const mockRoutesSummaryData = {
  empty: [],
  default: [
    {
      shipmentIds: [1, 2],
      shipmentsCount: 2,
      shipmentsPallets: 5,
      shipmentsPieces: 5,
      shipmentsHandlingUnits: 5,
      handlingUnits: 5,
      shipmentsWeight: 400,
      shipmentsWeightUnit: "lbs",
      id: 1,
      title: "First Mocked Route",
      name: "First Mocked Route",
      type: "Manual",
      isAvailableForMassage: true,
      door: 11,
      zone: "South LA County Mid",
      location: "11",
      startTime: new Date(),
      endTime: null,
      endPoint: null,
      driver: null,
      trailer: {
        id: 1061,
        name: "DYLT161625",
        isActive: true,
      },
      tractor: null,
      straightTruck: null,
      status: "CPLN",
      shipmentsTags: [
        {
          id: 7,
          name: "OSAD",
          description: "Overage, Shortage or Damaged",
          colorHex: "#FF8F7A",
        },
      ],
      allShipmentsPlanned: true,
      bills: 1,
      pallets: 1,
      pieces: 0,
      weight: 600,
      weightUnit: "lbs",
      tags: [
        {
          id: 7,
          name: "OSAD",
          description: "Overage, Shortage or Damaged",
          colorHex: "#FF8F7A",
        },
      ],
      doorId: 11,
      allFuturePlanDates: [20240417, 20240418, 20240419],
    },
    {
      shipmentIds: [3],
      shipmentsCount: 1,
      shipmentsPallets: 4,
      shipmentsPieces: 4,
      shipmentsHandlingUnits: 4,
      handlingUnits: 4,
      shipmentsWeight: 400,
      shipmentsWeightUnit: "lbs",
      id: 2,
      title: "Second Mocked Route",
      name: "Second Mocked Route",
      type: "Manual",
      isAvailableForMassage: true,
      door: 11,
      zone: "South LA County Mid",
      location: "11",
      startTime: new Date(),
      endTime: null,
      endPoint: null,
      driver: null,
      trailer: {
        id: 1061,
        name: "DYLT161625",
        isActive: true,
      },
      tractor: null,
      straightTruck: null,
      status: "CPLN",
      shipmentsTags: [
        {
          id: 7,
          name: "OSAD",
          description: "Overage, Shortage or Damaged",
          colorHex: "#FF8F7A",
        },
      ],
      allShipmentsPlanned: false,
      bills: 1,
      pallets: 1,
      pieces: 0,
      weight: 600,
      weightUnit: "lbs",
      tags: [
        {
          id: 7,
          name: "OSAD",
          description: "Overage, Shortage or Damaged",
          colorHex: "#FF8F7A",
        },
      ],
      doorId: 2,
      allFuturePlanDates: [20240417, 20240418, 20240419],
    },
    {
      shipmentIds: [],
      shipmentsCount: 0,
      shipmentsPallets: 0,
      shipmentsPieces: 0,
      shipmentsHandlingUnits: 0,
      handlingUnits: 0,
      shipmentsWeight: 0,
      shipmentsWeightUnit: "lbs",
      id: 3,
      title: "Third Mocked Route",
      name: "Third Mocked Route",
      type: "Manual",
      isAvailableForMassage: true,
      door: 11,
      zone: "South LA County Mid",
      location: "11",
      startTime: new Date(),
      endTime: null,
      endPoint: null,
      driver: null,
      trailer: {
        id: 1061,
        name: "DYLT161625",
        isActive: true,
      },
      tractor: null,
      straightTruck: null,
      status: "CPLN",
      shipmentsTags: [
        {
          id: 7,
          name: "OSAD",
          description: "Overage, Shortage or Damaged",
          colorHex: "#FF8F7A",
        },
      ],
      allShipmentsPlanned: false,
      bills: 1,
      pallets: 1,
      pieces: 0,
      weight: 600,
      weightUnit: "lbs",
      tags: [
        {
          id: 7,
          name: "OSAD",
          description: "Overage, Shortage or Damaged",
          colorHex: "#FF8F7A",
        },
      ],
      doorId: 2,
      allFuturePlanDates: [20240417, 20240418, 20240419],
    },
  ],
} as const satisfies Record<string, Route[]>;

export const mockAssociatedShipments: AssociatedShipment[] = [
  {
    shipmentId: 1,
    proNumber: "892728121",
    serviceDueDate: new Date("2026-04-01"),
    pallets: 3,
    weight: 314,
    doh: 2,
    tripNumber: "TRAP-34892",
    appointmentDate: new Date("2026-03-27T11:30:00"),
  },
  {
    shipmentId: 2,
    proNumber: "892728122",
    serviceDueDate: new Date("2026-03-25"),
    pallets: 3,
    weight: 314,
    doh: 2,
    tripNumber: "2893931",
    appointmentDate: null,
  },
];
